import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate, formatDateTime, formatTime } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Building,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  MessageSquare,
  Loader2,
  DollarSign,
  UserPlus,
  Copy,
  Send,
  Download,
  Ban,
  Star,
  Edit,
  Trash2,
  Users,
  Lock,
  ArrowDownCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, employee, isAdmin, sessionToken } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Role-based permissions
  const userRole = employee?.role || 'Employee';
  const canViewExpense = false; // Expense handling removed from tickets - use Daily Expenses module
  const canEditExpense = false;
  const isAdminHead = userRole === 'Admin Head' || userRole === 'Super Admin';
  const isITRole = ['IT Team', 'IT Head', 'Super Admin'].includes(userRole);
  const canDeEscalate = ['IT Head', 'Super Admin'].includes(userRole);
  
  // Dialogs
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [reassignDialog, setReassignDialog] = useState(false);
  const [reassignableUsers, setReassignableUsers] = useState([]);
  const [reassignTarget, setReassignTarget] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [cancelDialog, setCancelDialog] = useState(false);
  const [ratingDialog, setRatingDialog] = useState(false);
  const [deEscalateDialog, setDeEscalateDialog] = useState(false);
  const [deEscalateReason, setDeEscalateReason] = useState('');
  
  // Form states
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusRemarks, setStatusRemarks] = useState('');
  const [adminTeam, setAdminTeam] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingAdditionalComments, setRatingAdditionalComments] = useState('');
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);

  // IT Internal Comments state (IT-only, not visible to requester)
  const [itComments, setITComments] = useState([]);
  const [itCommentDraft, setITCommentDraft] = useState('');
  const [itCommentSending, setITCommentSending] = useState(false);

  // IT Completion validation (Category/Subcategory required to mark IT ticket as Completed)
  const [itTaxonomy, setITTaxonomy] = useState([]);
  const [completionCategory, setCompletionCategory] = useState('');
  const [completionSubcategory, setCompletionSubcategory] = useState('');

  const fetchTicket = useCallback(async () => {
    try {
      const res = await fetch(`${API}/tickets/${ticketId}`, {
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        setTicket(data);
      } else {
        toast.error('Ticket not found');
        navigate('/tickets');
      }
    } catch (error) {
      console.error('Fetch ticket error:', error);
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId, getAuthHeader, navigate]);

  const fetchAdminTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/team-members`, {
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        setAdminTeam(data);
      }
    } catch (error) {
      console.error('Fetch admin team error:', error);
    }
  }, [getAuthHeader]);

  const [rematching, setRematching] = useState(false);
  const canManageSpoc = ['Super Admin', 'Admin Head'].includes(employee?.role);
  const canSendFeedback = ['Super Admin', 'Admin Head', 'Management'].includes(employee?.role);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const sendCfvFeedback = async () => {
    setSendingFeedback(true);
    try {
      const res = await fetch(`${API}/cfv-feedback/trigger/${ticketId}`, { method: 'POST', headers: getAuthHeader() });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed to send feedback'); return; }
      toast.success(data.message || 'Feedback sent');
    } catch (e) {
      toast.error('Network error while sending feedback');
    } finally { setSendingFeedback(false); }
  };
  const rematchSpocs = async () => {
    setRematching(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/rematch-spocs`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Re-match failed'); return; }
      const added = data.newly_added || [];
      toast.success(added.length ? `Informed ${added.length} new SPOC(s): ${added.join(', ')}` : 'No new SPOCs matched. Check SPOC Management mappings.');
      fetchTicket();
    } catch (e) {
      toast.error('Network error during re-match');
    } finally { setRematching(false); }
  };


  useEffect(() => {
    fetchTicket();
    if (isAdmin) {
      fetchAdminTeam();
    }
  }, [fetchTicket, fetchAdminTeam, isAdmin]);

  // Chat functions
  const fetchChatMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/chat`, {
        headers: getAuthHeader(),
        
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(data);
      }
    } catch (error) {
      console.error('Fetch chat error:', error);
    }
  }, [ticketId, getAuthHeader]);

  useEffect(() => {
    if (ticket) {
      fetchChatMessages();
      // Poll for new messages every 15 seconds
      const interval = setInterval(fetchChatMessages, 15000);
      return () => clearInterval(interval);
    }
  }, [ticket, fetchChatMessages]);

  // IT Internal Comments — IT-only, polls every 15s on IT tickets
  const fetchITComments = useCallback(async () => {
    if (!ticket || ticket.module !== 'IT' || !isITRole) return;
    try {
      const res = await fetch(`${API}/it/tickets/${ticketId}/comments`, { headers: getAuthHeader() });
      if (res.ok) setITComments(await res.json());
    } catch (e) {
      console.error('Fetch IT comments error:', e);
    }
  }, [ticket, ticketId, getAuthHeader, isITRole]);

  useEffect(() => {
    if (!ticket || ticket.module !== 'IT' || !isITRole) return;
    fetchITComments();
    const id = setInterval(fetchITComments, 15000);
    return () => clearInterval(id);
  }, [ticket, isITRole, fetchITComments]);

  // Load IT taxonomy when the Status dialog is opened for an IT ticket
  useEffect(() => {
    if (!statusDialog || !ticket || ticket.module !== 'IT') return;
    if (itTaxonomy.length > 0) return;
    (async () => {
      try {
        const res = await fetch(`${API}/it-request-types`, { headers: getAuthHeader() });
        if (res.ok) setITTaxonomy(await res.json());
      } catch (e) {
        console.error('Failed to load IT taxonomy:', e);
      }
    })();
  }, [statusDialog, ticket, itTaxonomy.length, getAuthHeader]);

  const handleSendITComment = async () => {
    const msg = itCommentDraft.trim();
    if (!msg) return;
    setITCommentSending(true);
    try {
      const res = await fetch(`${API}/it/tickets/${ticketId}/comments`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      if (res.ok) {
        setITCommentDraft('');
        fetchITComments();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to post comment');
      }
    } catch (e) {
      toast.error('Network error posting comment');
    } finally {
      setITCommentSending(false);
    }
  };

  const handleSendChat = async () => {
    if (!chatMessage.trim()) return;
    
    setChatSending(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/chat`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({ message: chatMessage.trim() })
      });

      if (res.ok) {
        setChatMessage('');
        fetchChatMessages();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setChatSending(false);
    }
  };

  const canChat = () => {
    if (!ticket || !employee) return false;
    const isRequester = ticket.requester_email === employee.email;
    const isAssigned = ticket.assigned_admin_id === employee.employee_id;
    const isAssignedIT = (ticket.assigned_it_email || '').toLowerCase() === (employee.email || '').toLowerCase();
    const isPrivileged = ['Admin Head', 'Super Admin', 'IT Head', 'IT Team'].includes(userRole);
    const isManager = ticket.approver_id === employee.employee_id || (ticket.requester_details?.line_manager_email === employee.email);
    const isInformed = (ticket.informed_user_emails || []).map(e => (e || '').toLowerCase()).includes((employee.email || '').toLowerCase());
    return isRequester || isAssigned || isAssignedIT || isPrivileged || isManager || isInformed;
  };

  const canApprove = () => {
    if (!ticket || !employee) return false;
    if (ticket.status !== 'Open') return false;
    
    // Check if current user is the requester's manager or Super Admin
    // Admin Head (location admin) cannot approve tickets
    const requesterManagerEmail = ticket.requester_details?.line_manager_email;
    return requesterManagerEmail === employee.email || employee.role === 'Super Admin';
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/approve?remarks=${encodeURIComponent(approvalRemarks)}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Ticket approved successfully');
        setApproveDialog(false);
        fetchTicket();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to approve ticket');
      }
    } catch (error) {
      toast.error('Failed to approve ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/reject?reason=${encodeURIComponent(rejectionReason)}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Ticket rejected');
        setRejectDialog(false);
        fetchTicket();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reject ticket');
      }
    } catch (error) {
      toast.error('Failed to reject ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    // IT Completion validation — Category & Subcategory mandatory
    if (ticket?.module === 'IT' && newStatus === 'Completed') {
      if (!completionCategory || !completionSubcategory) {
        toast.error('Category and Subcategory are mandatory to mark an IT ticket as Completed');
        return;
      }
    }
    setActionLoading(true);
    try {
      const payload = {
        status: newStatus,
        approval_remarks: statusRemarks
      };
      if (ticket?.module === 'IT' && newStatus === 'Completed') {
        payload.completion_category = completionCategory;
        payload.completion_subcategory = completionSubcategory;
      }
      const res = await fetch(`${API}/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Status updated successfully');
        setStatusDialog(false);
        setCompletionCategory('');
        setCompletionSubcategory('');
        fetchTicket();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedAdmin) {
      toast.error('Please select an admin team member');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/assign?admin_id=${selectedAdmin}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Ticket assigned successfully');
        setAssignDialog(false);
        fetchTicket();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to assign ticket');
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitDraft = async () => {
    // Frontend validation - check mandatory fields based on request type
    if (ticket?.request_type === 'Onboarding' || ticket?.request_type === 'HR – Induction Kit Request') {
      const missingFields = [];
      if (!ticket.induction_employee_name?.trim()) missingFields.push('Employee Name');
      if (!ticket.induction_employee_id?.trim()) missingFields.push('Employee ID');
      if (!ticket.induction_designation?.trim()) missingFields.push('Designation');
      if (!ticket.induction_joining_date) missingFields.push('Joining Date');
      if (!ticket.induction_location?.trim()) missingFields.push('Location');
      if (!ticket.induction_department?.trim()) missingFields.push('Department');
      if (!ticket.induction_employee_type) missingFields.push('Employee Type');
      if (!ticket.induction_entity) missingFields.push('Entity');
      
      if (missingFields.length > 0) {
        toast.error(`Please fill all mandatory fields before submitting: ${missingFields.join(', ')}`);
        return;
      }
    } else if (ticket?.request_type === 'Insurance Removal Request') {
      const missingFields = [];
      if (!ticket.insurance_employee_name?.trim()) missingFields.push('Employee Name');
      if (!ticket.insurance_employee_id?.trim()) missingFields.push('Employee ID');
      if (!ticket.insurance_exit_date) missingFields.push('Date of Exit');
      
      if (missingFields.length > 0) {
        toast.error(`Please fill all mandatory fields before submitting: ${missingFields.join(', ')}`);
        return;
      }
    } else if (!ticket?.description?.trim() && !ticket?.request_type?.includes('Stationery')) {
      toast.error('Please provide a description before submitting');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/submit-draft`, {
        method: 'PUT',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Draft submitted for approval');
        fetchTicket();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit draft');
      }
    } catch (error) {
      toast.error('Failed to submit draft');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    
    setActionLoading(true);
    try {
      const params = new URLSearchParams({
        cancellation_reason: cancellationReason
      });
      
      const res = await fetch(`${API}/tickets/${ticketId}/cancel?${params}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Ticket cancelled');
        setCancelDialog(false);
        setCancellationReason('');
        fetchTicket();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to cancel ticket');
      }
    } catch (error) {
      toast.error('Failed to cancel ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeEscalate = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/de-escalate`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: deEscalateReason }),
      });
      if (res.ok) {
        toast.success('Ticket removed from Escalation');
        setDeEscalateDialog(false);
        setDeEscalateReason('');
        fetchTicket();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to remove ticket from Escalation');
      }
    } catch (e) {
      toast.error('Failed to remove ticket from Escalation');
    } finally {
      setActionLoading(false);
    }
  };

  const openReassignDialog = async () => {
    setReassignDialog(true);
    setReassignTarget('');
    setReassignReason('');
    try {
      // Fetch all IT Team / IT Head users — reassignment is NOT restricted by location
      const res = await fetch(`${API}/employees`, { headers: getAuthHeader() });
      if (res.ok) {
        const all = await res.json();
        const candidates = all.filter(e =>
          ['IT Team', 'IT Head'].includes(e.role) &&
          e.is_active !== false &&
          (e.email || '').toLowerCase() !== (ticket?.assigned_it_email || '').toLowerCase()
        );
        setReassignableUsers(candidates);
      }
    } catch (e) { /* ignore */ }
  };

  const submitReassign = async () => {
    if (!reassignTarget) {
      toast.error('Pick a new assignee');
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/reassign`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_assignee_email: reassignTarget, reason: reassignReason || '' }),
      });
      if (res.ok) {
        toast.success('Ticket reassigned');
        setReassignDialog(false);
        fetchTicket();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to reassign');
      }
    } catch (e) {
      toast.error('Failed to reassign');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = async () => {    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/duplicate`, {
        method: 'POST',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Ticket duplicated! Redirecting to draft...');
        navigate(`/tickets/${data.ticket.ticket_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to duplicate ticket');
      }
    } catch (error) {
      toast.error('Failed to duplicate ticket');
    } finally {
      setActionLoading(false);
    }
  };

  // Rating dropdown options change dynamically based on selected rating
  // Feedback options are reused across modules — swap "Admin team" → "IT team" for IT tickets
  const _teamLabel = ticket?.module === 'IT' ? 'IT team' : 'Admin team';
  const RATING_OPTIONS = {
    5: [
      'Very satisfied with the support provided',
      'Quick and efficient service',
      'Issue resolved smoothly',
      `Excellent coordination from ${_teamLabel}`,
      'Great experience overall',
    ],
    4: [
      'Service met expectations',
      `Good support from ${_teamLabel}`,
      'Minor improvements can be made',
      'Overall satisfactory experience',
      'Response was timely',
    ],
    3: [
      'Response time can be improved',
      'Follow-up required for closure',
      'Communication can be clearer',
      'Improvement needed in process',
    ],
    2: [
      'Delay in handling the request',
      'Issue not fully resolved',
      'Communication was not clear',
      'Support needs improvement',
      'Unsatisfactory handling',
    ],
    1: [
      'Overall experience was not satisfactory',
      'Issue not resolved',
      'Very poor communication',
      'Significant delay',
      'Other',
    ],
  };

  const handleRating = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    // Dropdown selection mandatory for ALL ratings
    if (!ratingComment || !ratingComment.trim()) {
      toast.error('Please select a feedback option');
      return;
    }

    // For rating = 1 with "Other" selected, additional comments required
    if (rating === 1 && ratingComment === 'Other' && !ratingAdditionalComments.trim()) {
      toast.error('Please describe your feedback in the comments field');
      return;
    }
    
    setActionLoading(true);
    try {
      const params = new URLSearchParams({
        rating: rating.toString()
      });
      if (ratingComment) {
        params.append('comment', ratingComment);
      }
      if (rating === 1 && ratingComment === 'Other' && ratingAdditionalComments.trim()) {
        params.append('additional_comments', ratingAdditionalComments.trim());
      }
      
      const res = await fetch(`${API}/tickets/${ticketId}/rate?${params}`, {
        method: 'POST',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Rating submitted');
        setRatingDialog(false);
        setRating(0);
        setRatingComment('');
        setRatingAdditionalComments('');
        fetchTicket();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit rating');
      }
    } catch (error) {
      toast.error('Failed to submit rating');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!window.confirm('Are you sure you want to delete this draft? This action cannot be undone.')) {
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Draft deleted');
        navigate('/tickets');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete draft');
      }
    } catch (error) {
      toast.error('Failed to delete draft');
    } finally {
      setActionLoading(false);
    }
  };

  // Render request-specific details based on request type
  const renderRequestDetails = () => {
    const hasDetails = 
      ticket.courier_type || ticket.guest_name || ticket.event_name || 
      ticket.room_type || ticket.stationery_items || 
      ticket.induction_employee_name || ticket.insurance_employee_name ||
      ticket.promo_employee_name || ticket.promo_employees?.length > 0 ||
      ticket.birthday_employee_name || ticket.birthday_employees?.length > 0 ||
      ticket.insurance_add_employees?.length > 0 ||
      ticket.maintenance_category ||
      ticket.other_request_type ||
      ticket.cfv_guest_name || ticket.cfv_company || (ticket.cfv_visitors && ticket.cfv_visitors.length > 0) || (ticket.cfv_visit_locations && ticket.cfv_visit_locations.length > 0) ||
      (ticket.informed_departments && ticket.informed_departments.length > 0) ||
      (ticket.custom_fields && Object.keys(ticket.custom_fields).length > 0);

    if (!hasDetails) return null;

    // Format date values from yyyy-mm-dd to dd-mm-yyyy
    const formatDetailValue = (val) => {
      if (!val) return val;
      const str = String(val);
      // Match yyyy-mm-dd pattern
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split('-');
        return `${d}-${m}-${y}`;
      }
      // Match yyyy-mm-ddTHH:mm:ss (ISO datetime)
      if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
        const date = new Date(str);
        if (!isNaN(date.getTime())) {
          const d = String(date.getDate()).padStart(2, '0');
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const y = date.getFullYear();
          return `${d}-${m}-${y}`;
        }
      }
      return str;
    };

    const DetailRow = ({ label, value }) => {
      if (!value) return null;
      return (
        <div className="flex justify-between gap-2 py-2 border-b border-border/50 last:border-0">
          <span className="text-muted-foreground shrink-0">{label}</span>
          <span className="font-medium text-right break-words min-w-0">{formatDetailValue(value)}</span>
        </div>
      );
    };

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Request Details
          </CardTitle>
          <CardDescription>
            Additional information for this {ticket.request_type} request
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {/* Courier Details */}
          {ticket.courier_type && (
            <>
              <DetailRow label="Courier Type" value={ticket.courier_type} />
              <DetailRow label="From" value={ticket.courier_from} />
              <DetailRow label="To" value={ticket.courier_to} />
              <DetailRow label="Contents" value={ticket.courier_contents} />
            </>
          )}

          {/* Client Factory Visit Details */}
          {(ticket.cfv_guest_name || ticket.cfv_company || (ticket.cfv_visitors && ticket.cfv_visitors.length > 0) || (ticket.cfv_visit_locations && ticket.cfv_visit_locations.length > 0)) && (
            <>
              {ticket.cfv_guest_name && <DetailRow label="Guest Name" value={ticket.cfv_guest_name} />}
              {ticket.cfv_company && <DetailRow label="Company" value={ticket.cfv_company} />}
              {ticket.cfv_guests_count && <DetailRow label="No. of Guests" value={ticket.cfv_guests_count} />}
              {ticket.cfv_brand && <DetailRow label="Brand to Visit" value={ticket.cfv_brand} />}
              {ticket.cfv_brands && ticket.cfv_brands.length > 0 && <DetailRow label="Brand(s) to Visit" value={ticket.cfv_brands.join(', ')} />}
              {ticket.cfv_purpose && <DetailRow label="Purpose" value={ticket.cfv_purpose} />}
              {ticket.cfv_primary_objective && <DetailRow label="Primary Purpose of Visit" value={ticket.cfv_primary_objective === 'Other' ? (ticket.cfv_primary_objective_other || 'Other') : ticket.cfv_primary_objective} />}
              {ticket.cfv_project_linked && <DetailRow label="Linked to Project" value={ticket.cfv_project_linked} />}
              {ticket.cfv_project_name && <DetailRow label="Project Name" value={ticket.cfv_project_name} />}
              {ticket.cfv_project_location && <DetailRow label="Project Location" value={ticket.cfv_project_location} />}
              {ticket.cfv_project_stage && <DetailRow label="Project Stage" value={ticket.cfv_project_stage} />}
              {ticket.cfv_industry_segment && <DetailRow label="Industry / Segment" value={ticket.cfv_industry_segment === 'Other' ? (ticket.cfv_industry_segment_other || 'Other') : ticket.cfv_industry_segment} />}
              {ticket.cfv_area_of_interest && <DetailRow label="Area of Interest" value={ticket.cfv_area_of_interest} />}
              {ticket.cfv_need_invite && <DetailRow label="Need Invite" value={ticket.cfv_need_invite} />}
              {ticket.cfv_transport && <DetailRow label="Transport" value={ticket.cfv_transport} />}
              {ticket.cfv_car_parking && <DetailRow label="Car Parking Required" value={ticket.cfv_car_parking} />}
              {ticket.cfv_photography && <DetailRow label="Photography" value={ticket.cfv_photography} />}
              {ticket.cfv_videography && <DetailRow label="Videography" value={ticket.cfv_videography} />}
              {ticket.cfv_food_required && <DetailRow label="Food Required" value={ticket.cfv_food_required} />}
              {ticket.cfv_meals && ticket.cfv_meals.length > 0 && <DetailRow label="Meals" value={ticket.cfv_meals.join(', ')} />}
              {ticket.cfv_dietary && <DetailRow label="Dietary Preference" value={ticket.cfv_dietary} />}
              {ticket.cfv_stay_required && <DetailRow label="Stay Required" value={ticket.cfv_stay_required} />}
              {ticket.cfv_stay_required === 'Yes' && ticket.cfv_stay_days && <DetailRow label="Number of Nights" value={ticket.cfv_stay_days} />}
              {ticket.cfv_giveaway && ticket.cfv_giveaway.length > 0 && <DetailRow label="Giveaway / Momento" value={ticket.cfv_giveaway.join(', ')} />}
              {ticket.cfv_giveaway_other && <DetailRow label="Giveaway (Other)" value={ticket.cfv_giveaway_other} />}
              {ticket.cfv_areas_covered && <DetailRow label="Areas / Facilities to be covered" value={ticket.cfv_areas_covered} />}
              {ticket.cfv_products_demonstrated && <DetailRow label="Products / Solutions to be demonstrated" value={ticket.cfv_products_demonstrated} />}
              {ticket.cfv_lab_testing && <DetailRow label="Lab Testing / Demonstration" value={ticket.cfv_lab_testing} />}
              {ticket.cfv_lab_testing_details && <DetailRow label="Lab Testing Details" value={ticket.cfv_lab_testing_details} />}
              {ticket.cfv_team_members && <DetailRow label="Unitile Team Members Required" value={ticket.cfv_team_members} />}
              {ticket.cfv_senior_mgmt && <DetailRow label="Senior Management Presence" value={ticket.cfv_senior_mgmt} />}
              {ticket.cfv_presentation && <DetailRow label="Presentation Required" value={ticket.cfv_presentation} />}
              {ticket.cfv_presentation_type && <DetailRow label="Type of Presentation" value={ticket.cfv_presentation_type} />}
              {ticket.cfv_conference_room && <DetailRow label="Conference Room Required" value={ticket.cfv_conference_room} />}
              {ticket.cfv_visitors && ticket.cfv_visitors.length > 0 && (
                <div className="pt-3" data-testid="cfv-detail-visitors">
                  <p className="text-sm font-semibold mb-2">Visitors ({ticket.cfv_visitors.length})</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="py-1.5 pr-3">Name</th><th className="py-1.5 pr-3">Contact</th><th className="py-1.5 pr-3">Email</th>
                          <th className="py-1.5 pr-3">Company</th><th className="py-1.5 pr-3">Designation</th><th className="py-1.5 pr-3">Country</th>{ticket.cfv_food_required === 'Yes' && <th className="py-1.5 pr-3">Dietary</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {ticket.cfv_visitors.map((v, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1.5 pr-3 font-medium">{v.name || '—'}</td>
                            <td className="py-1.5 pr-3">{v.contact || '—'}</td>
                            <td className="py-1.5 pr-3">{v.email || '—'}</td>
                            <td className="py-1.5 pr-3">{v.company || '—'}</td>
                            <td className="py-1.5 pr-3">{v.designation || '—'}</td>
                            <td className="py-1.5 pr-3">{v.country || '—'}</td>
                            {ticket.cfv_food_required === 'Yes' && <td className="py-1.5 pr-3">{v.dietary || '—'}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {ticket.cfv_visit_locations && ticket.cfv_visit_locations.length > 0 && (
                <div className="pt-3" data-testid="cfv-detail-itinerary">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold">Visit Itinerary</p>
                    {canManageSpoc && (
                      <Button size="sm" variant="outline" onClick={rematchSpocs} disabled={rematching} data-testid="rematch-spocs-btn">
                        {rematching ? 'Matching…' : 'Re-run SPOC matching'}
                      </Button>
                    )}
                    {canSendFeedback && (
                      <Button size="sm" variant="outline" onClick={sendCfvFeedback} disabled={sendingFeedback} data-testid="send-cfv-feedback-btn">
                        {sendingFeedback ? 'Sending…' : 'Send Feedback to Visitors'}
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="py-1.5 pr-3">Location</th><th className="py-1.5 pr-3">Brand</th><th className="py-1.5 pr-3">Entity</th>
                          <th className="py-1.5 pr-3">Arrival</th><th className="py-1.5 pr-3">Departure</th><th className="py-1.5 pr-3">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ticket.cfv_visit_locations.map((s, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1.5 pr-3 font-medium">{s.location}</td>
                            <td className="py-1.5 pr-3">{s.brand || '—'}</td>
                            <td className="py-1.5 pr-3">{s.entity || '—'}</td>
                            <td className="py-1.5 pr-3">{formatDetailValue(s.arrival_date)} {s.expected_time ? formatTime(s.expected_time) : ''}</td>
                            <td className="py-1.5 pr-3">{s.departure_date ? formatDetailValue(s.departure_date) : '—'} {s.departure_time ? formatTime(s.departure_time) : ''}</td>
                            <td className="py-1.5 pr-3">{s.duration}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}


          {/* Guest Arrival Details */}
          {ticket.guest_name && (            <>
              <DetailRow label="Guest Name" value={ticket.guest_name} />
              {ticket.guest_company && <DetailRow label="Company" value={ticket.guest_company} />}
              {ticket.guest_purpose && <DetailRow label="Purpose" value={ticket.guest_purpose} />}
              {ticket.guest_arrival_date && <DetailRow label="Arrival Date" value={ticket.guest_arrival_date} />}
              {ticket.guest_departure_date && <DetailRow label="Departure Date" value={ticket.guest_departure_date} />}
              {ticket.guest_date && <DetailRow label="Date" value={ticket.guest_date} />}
              {ticket.guest_time && <DetailRow label="Time" value={formatTime(ticket.guest_time)} />}
              {ticket.guest_food_required && <DetailRow label="Food Required" value={ticket.guest_food_required} />}
              {ticket.guest_car_parking && <DetailRow label="Car Parking" value={ticket.guest_car_parking} />}
              {ticket.guest_vehicle_number && <DetailRow label="Vehicle Number" value={ticket.guest_vehicle_number} />}
            </>
          )}

          {/* Event Details */}
          {ticket.event_name && (
            <>
              <DetailRow label="Event Name" value={ticket.event_name} />
              <DetailRow label="Date" value={ticket.event_date} />
              <DetailRow label="Time" value={formatTime(ticket.event_time)} />
              <DetailRow label="Location" value={ticket.event_location} />
              <DetailRow label="Attendees" value={ticket.event_attendees} />
              <DetailRow label="Requirements" value={ticket.event_requirements} />
            </>
          )}

          {/* Room Booking Details */}
          {ticket.room_type && (
            <>
              <DetailRow label="Location" value={ticket.room_location} />
              <DetailRow label="Room" value={ticket.room_type} />
              <DetailRow label="Date" value={ticket.room_date} />
              <DetailRow label="Start Time" value={formatTime(ticket.room_start_time)} />
              <DetailRow label="End Time" value={formatTime(ticket.room_end_time)} />
              <DetailRow label="Attendees" value={ticket.room_attendees} />
              <DetailRow label="Meeting Category" value={ticket.room_meeting_category} />
              <DetailRow label="Requirements" value={ticket.room_requirements} />
            </>
          )}

          {/* Stationery Request Details */}
          {ticket.stationery_items && (
            <>
              <DetailRow label="Items" value={ticket.stationery_items} />
              <DetailRow label="Quantity" value={ticket.stationery_quantity} />
              <DetailRow label="Description" value={ticket.stationery_description} />
              <DetailRow label="Required By" value={ticket.stationery_required_by_date} />
            </>
          )}

          {/* Onboarding Details */}
          {ticket.induction_employee_name && (
            <>
              <div className="text-sm font-semibold text-primary mt-2 mb-1">Basic Details</div>
              <DetailRow label="Employee Name" value={ticket.induction_employee_name} />
              <DetailRow label="Employee ID" value={ticket.induction_employee_id} />
              <DetailRow label="Designation" value={ticket.induction_designation} />
              <DetailRow label="Joining Date" value={ticket.induction_joining_date} />
              <DetailRow label="Location" value={ticket.induction_location} />
              <DetailRow label="Department" value={ticket.induction_department} />
              <DetailRow label="Employee Type" value={ticket.induction_employee_type} />
              <DetailRow label="T-Shirt Size" value={ticket.induction_tshirt_size} />
              <DetailRow label="Entity" value={ticket.induction_entity} />
              <DetailRow label="Food Required" value={ticket.induction_food_required} />
              
              
              {ticket.induction_need_id_card === 'yes' && (
                <>
                  <div className="text-sm font-semibold text-primary mt-3 mb-1">ID Card Details</div>
                  <DetailRow label="Date of Birth" value={ticket.induction_dob} />
                  <DetailRow label="Date of Issue" value={ticket.induction_date_of_issue} />
                  <DetailRow label="Blood Group" value={ticket.induction_blood_group} />
                  <DetailRow label="Emergency Contact" value={ticket.induction_emergency_number} />
                  <DetailRow label="Aadhaar Number" value={ticket.induction_masked_aadhaar ? `XXXX-XXXX-${ticket.induction_masked_aadhaar}` : ''} />
                  <DetailRow label="Office Address" value={ticket.induction_office_address} />
                </>
              )}
              
              {ticket.induction_need_mediclaim === 'yes' && ticket.induction_mediclaim_members?.length > 0 && (
                <>
                  <div className="text-sm font-semibold text-primary mt-3 mb-1">Mediclaim Members</div>
                  {ticket.induction_mediclaim_members.map((member, idx) => (
                    <div key={idx} className="bg-muted/50 p-3 rounded-md mb-2">
                      <p className="font-medium">{member.full_name || member.name || 'N/A'}</p>
                      <p className="text-sm text-muted-foreground">
                        {member.relation}{member.dob ? ` • DOB: ${member.dob}` : ''}
                      </p>
                      {(member.doj || member.grade || member.sum_insured) && (
                        <p className="text-sm text-muted-foreground">
                          {member.doj ? `DOJ: ${member.doj}` : ''}
                          {member.grade ? `${member.doj ? ' • ' : ''}Grade: ${member.grade}` : ''}
                          {member.sum_insured ? `${(member.doj || member.grade) ? ' • ' : ''}Sum Insured: ₹${member.sum_insured}` : ''}
                        </p>
                      )}
                    </div>
                  ))}
                </>
              )}

              {(ticket.induction_facility_visiting_card || ticket.induction_facility_hotel || ticket.induction_facility_transport) && (
                <>
                  <div className="text-sm font-semibold text-primary mt-3 mb-1">Facility Requirements</div>
                  <div className="flex flex-wrap gap-2">
                    {ticket.induction_facility_visiting_card && (
                      <Badge variant="secondary">Visiting Card</Badge>
                    )}
                    {ticket.induction_facility_hotel && (
                      <Badge variant="secondary">Hotel</Badge>
                    )}
                    {ticket.induction_facility_transport && (
                      <Badge variant="secondary">Transport</Badge>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* Insurance Removal Details */}
          {(ticket.insurance_employee_name || (ticket.insurance_employees && ticket.insurance_employees.length > 0)) && (
            <>
              {ticket.insurance_employees && ticket.insurance_employees.length > 0 ? (
                <>
                  <div className="text-sm font-semibold text-primary mt-2 mb-1">Employees for Insurance Removal</div>
                  {ticket.insurance_employees.map((emp, idx) => (
                    <div key={idx} className="bg-muted/50 p-3 rounded-md mb-2">
                      <p className="font-medium">{emp.name} ({emp.id})</p>
                      <p className="text-sm text-muted-foreground">
                        {emp.department && `${emp.department} • `}{emp.relation && `${emp.relation} • `}DOB: {emp.dob || emp.exit_date}
                      </p>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <DetailRow label="Employee Name" value={ticket.insurance_employee_name} />
                  <DetailRow label="Employee ID" value={ticket.insurance_employee_id} />
                  <DetailRow label="Date of Exit" value={ticket.insurance_exit_date} />
                </>
              )}
            </>
          )}

          {/* Promotion Box Details */}
          {(ticket.promo_employee_name || (ticket.promo_employees && ticket.promo_employees.length > 0) || ticket.promo_entity) && (
            <>
              <div className="text-sm font-semibold text-primary mt-2 mb-1">Promotion Box Request</div>
              <DetailRow label="Entity" value={ticket.promo_entity} />
              {ticket.promo_employees && ticket.promo_employees.length > 0 ? (
                ticket.promo_employees.map((emp, idx) => (
                  <div key={idx} className="bg-muted/50 p-3 rounded-md mb-2">
                    <p className="font-medium">{emp.employee_name} ({emp.employee_code})</p>
                    <p className="text-sm text-muted-foreground">
                      Address: {emp.employee_address}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Receiver: {emp.receiver_name} • {emp.receiver_address} • {emp.receiver_contact}
                    </p>
                  </div>
                ))
              ) : (
                <>
                  <DetailRow label="Employee Name" value={ticket.promo_employee_name} />
                  <DetailRow label="Designation" value={ticket.promo_designation} />
                  <DetailRow label="Department" value={ticket.promo_department} />
                  <DetailRow label="Effective Date" value={ticket.promo_effective_date} />
                  <DetailRow label="Special Instructions" value={ticket.promo_special_instructions} />
                </>
              )}
            </>
          )}

          {/* Desk Decor - Birthday Details */}
          {(ticket.birthday_employee_name || (ticket.birthday_employees && ticket.birthday_employees.length > 0)) && (
            <>
              <div className="text-sm font-semibold text-primary mt-2 mb-1">Birthday Celebration</div>
              {ticket.birthday_employees && ticket.birthday_employees.length > 0 ? (
                ticket.birthday_employees.map((emp, idx) => (
                  <div key={idx} className="bg-muted/50 p-3 rounded-md mb-2">
                    <p className="font-medium">{emp.employee_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Date: {emp.required_by_date} • Dept: {emp.department}
                    </p>
                  </div>
                ))
              ) : (
                <>
                  <DetailRow label="Employee Name" value={ticket.birthday_employee_name} />
                  <DetailRow label="Required By Time" value={ticket.birthday_time} />
                  <DetailRow label="Date" value={ticket.birthday_date} />
                  <DetailRow label="Department" value={ticket.birthday_department} />
                  <DetailRow label="Location" value={ticket.birthday_location} />
                </>
              )}
            </>
          )}

          {/* Insurance Addition Details */}
          {ticket.insurance_add_employees && ticket.insurance_add_employees.length > 0 && (
            <>
              <div className="text-sm font-semibold text-primary mt-2 mb-1">Insurance Addition</div>
              {ticket.insurance_add_employees.map((emp, idx) => (
                <div key={idx} className="bg-muted/50 p-3 rounded-md mb-2">
                  <p className="font-medium">{emp.employee_name} ({emp.employee_code})</p>
                  <p className="text-sm text-muted-foreground">
                    {emp.department} • Grade: {emp.grade} • Sum Insured: {emp.sum_insured}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Insurance For: {emp.insurance_required_for} • Name: {emp.member_name} • DOB: {emp.member_dob}
                  </p>
                </div>
              ))}
            </>
          )}

          {/* Custom Fields for Custom Request Types */}
          {ticket.custom_fields && Object.keys(ticket.custom_fields).length > 0 && (
            <>
              <div className="text-sm font-semibold text-primary mt-2 mb-1">Custom Details</div>
              {Object.entries(ticket.custom_fields).map(([key, value]) => (
                <DetailRow 
                  key={key} 
                  label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                  value={typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value} 
                />
              ))}
            </>
          )}

          {/* Repairs & Maintenance Details */}
          {ticket.maintenance_category && (
            <>
              <div className="text-sm font-semibold text-primary mt-2 mb-1">Repairs & Maintenance</div>
              <DetailRow label="Category" value={ticket.maintenance_category} />
              {ticket.maintenance_other_text && (
                <DetailRow label="Details" value={ticket.maintenance_other_text} />
              )}
            </>
          )}

          {/* Other Request Type */}
          {ticket.other_request_type && (
            <DetailRow label="Request Type" value={ticket.other_request_type} />
          )}

          {/* Departments to be Informed (SPOC) */}
          {ticket.informed_departments && ticket.informed_departments.length > 0 && (
            <DetailRow label="Departments to be Informed" value={ticket.informed_departments.join(', ')} />
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-8 w-48" />
          <Card>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!ticket) {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6" data-testid="ticket-detail-page">
        {/* Header */}
        <div className="flex items-start gap-2 sm:gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="shrink-0"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight font-mono break-all">
                {ticket.ticket_id}
              </h1>
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
            </div>
            <p className="text-sm sm:text-base text-muted-foreground mt-1 break-words">
              {ticket.request_type === 'Other' ? ticket.other_request_type : ticket.request_type}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 sm:gap-3 [&>button]:flex-1 sm:[&>button]:flex-none [&>button]:min-w-[calc(50%-0.25rem)] sm:[&>button]:min-w-0">
          {/* Draft Submit Button */}
          {ticket.status === 'Draft' && ticket.requester_email === employee?.email && (
            <>
              <Button 
                onClick={handleSubmitDraft}
                disabled={actionLoading}
                className="gap-2"
                data-testid="submit-draft-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit for Approval
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate(`/tickets/${ticketId}/edit`)}
                className="gap-2"
                data-testid="edit-draft-btn"
              >
                <Edit className="w-4 h-4" />
                Edit Draft
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteDraft}
                disabled={actionLoading}
                className="gap-2"
                data-testid="delete-draft-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete Draft
              </Button>
            </>
          )}

          {/* Edit Button for Open status */}
          {ticket.status === 'Open' && ticket.requester_email === employee?.email && (
            <Button 
              variant="outline"
              onClick={() => navigate(`/tickets/${ticketId}/edit`)}
              className="gap-2"
              data-testid="edit-ticket-btn"
            >
              <Edit className="w-4 h-4" />
              Edit Request
            </Button>
          )}

          {canApprove() && (
            <>
              <Button 
                onClick={() => setApproveDialog(true)}
                className="gap-2 bg-green-600 hover:bg-green-700"
                data-testid="approve-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Approve
              </Button>
              <Button 
                variant="destructive"
                onClick={() => setRejectDialog(true)}
                className="gap-2"
                data-testid="reject-btn"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </Button>
            </>
          )}
          
          {/* Update Status — Admin tickets for admin roles; IT tickets for IT roles + Super Admin */}
          {(
            (isAdmin && (['Approved', 'In Progress'].includes(ticket.status) || (isAdminHead && ticket.status === 'Completed'))) ||
            (ticket.module === 'IT' && ['IT Team', 'IT Head', 'Super Admin'].includes(userRole) && !['Completed', 'Cancelled', 'Rejected'].includes(ticket.status))
          ) && (
            <>
              <Button 
                variant="outline"
                onClick={() => {
                  setNewStatus(ticket.status);
                  setStatusDialog(true);
                }}
                className="gap-2"
                data-testid="update-status-btn"
              >
                <Clock className="w-4 h-4" />
                Update Status
              </Button>
              {isAdminHead && ticket.module !== 'IT' && (
                <Button 
                  variant="outline"
                  onClick={() => setAssignDialog(true)}
                  className="gap-2"
                  data-testid="assign-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  Assign
                </Button>
              )}
            </>
          )}

          {/* Remove from Escalation — IT Head + Super Admin only, on Escalated IT tickets */}
          {ticket.module === 'IT' && ticket.status === 'Escalated' && canDeEscalate && (
            <Button
              variant="outline"
              onClick={() => {
                setDeEscalateReason('');
                setDeEscalateDialog(true);
              }}
              className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
              data-testid="de-escalate-btn"
            >
              <ArrowDownCircle className="w-4 h-4" />
              Remove from Escalation
            </Button>
          )}
          
          {/* Cancel Button - Admin Head/Super Admin or ticket creator.
              Standard tickets: cancellable when not Cancelled/Completed.
              Meeting Room Booking: also cancellable when Completed (frees up the slot). Admin Team can also cancel MR bookings. */}
          {ticket.status !== 'Cancelled' && (() => {
            const isMR = ticket.request_type === 'Meeting Room Booking';
            const canCancelStandard = (isAdminHead || ticket.requester_email === employee?.email) && ticket.status !== 'Completed';
            const canCancelMR = isMR && (
              isAdminHead || userRole === 'Admin Team' || ticket.requester_email === employee?.email
            );
            return canCancelStandard || canCancelMR;
          })() && (
            <Button 
              variant="destructive"
              onClick={() => setCancelDialog(true)}
              className="gap-2"
              data-testid="cancel-btn"
            >
              <Ban className="w-4 h-4" />
              Cancel Request
            </Button>
          )}

          {/* Rate Button - For completed tickets (only for the requester, not admins).
              Meeting Room Booking is auto-completed and not eligible for rating. */}
          {ticket.status === 'Completed' && !ticket.rating && 
           ticket.request_type !== 'Meeting Room Booking' &&
           ticket.requester_email === employee?.email && !isAdminHead && userRole !== 'Admin Team' && (
            <Button 
              variant="outline"
              onClick={() => setRatingDialog(true)}
              className="gap-2"
              data-testid="rate-btn"
            >
              <Star className="w-4 h-4" />
              Rate Service
            </Button>
          )}

          {/* Duplicate Button - For ticket owner */}
          {ticket.requester_email === employee?.email && (
            <Button 
              variant="outline"
              onClick={handleDuplicate}
              disabled={actionLoading}
              className="gap-2"
              data-testid="duplicate-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
              Duplicate
            </Button>
          )}

          {/* Reassign button — IT tickets, visible to IT Team/IT Head/Super Admin */}
          {ticket.module === 'IT' && ['IT Team', 'IT Head', 'Super Admin'].includes(userRole) && ticket.status !== 'Completed' && ticket.status !== 'Cancelled' && (
            <Button
              variant="outline"
              onClick={openReassignDialog}
              className="gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
              data-testid="reassign-btn"
            >
              <Users className="w-4 h-4" />
              Reassign
            </Button>
          )}
        </div>

        {/* IT SLA / Escalation banner */}
        {ticket.module === 'IT' && (ticket.status === 'Escalated' || ticket.status === 'Completed' || ticket.sla_deadline) && (() => {
          // Completed + within SLA → On Time (green)
          // Completed + past SLA   → Breached (red)
          // Escalated              → SLA BREACHED (red)
          // Open/In Progress       → SLA Deadline (amber)
          let mode = 'deadline'; // 'deadline' | 'breached' | 'ontime'
          if (ticket.status === 'Escalated') mode = 'breached';
          else if (ticket.status === 'Completed' && ticket.sla_deadline) {
            const completedAt = ticket.completed_at || ticket.updated_at;
            mode = completedAt && new Date(completedAt) <= new Date(ticket.sla_deadline) ? 'ontime' : 'breached';
          }
          const styles = {
            breached: 'border-red-300 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300',
            ontime: 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300',
            deadline: 'border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300',
          };
          return (
            <div className={`rounded-lg border p-3 text-sm flex items-center gap-3 ${styles[mode]}`} data-testid="it-sla-banner">
              {mode === 'breached' && (
                <>
                  <span className="font-semibold">SLA BREACHED</span>
                  {ticket.status === 'Escalated' && (
                    <span>— Escalated{ticket.escalated_at ? ` on ${new Date(ticket.escalated_at).toLocaleString('en-IN')}` : ''}{ticket.assigned_it_name ? ` to ${ticket.assigned_it_name}` : ''}.</span>
                  )}
                  {ticket.status === 'Completed' && (
                    <span>— Completed after the 4-hour SLA window.</span>
                  )}
                </>
              )}
              {mode === 'ontime' && (
                <>
                  <span className="font-semibold">On Time</span>
                  <span>— Completed within the 4-hour SLA window.</span>
                </>
              )}
              {mode === 'deadline' && (
                <>
                  <span className="font-semibold">SLA Deadline:</span>
                  <span>{new Date(ticket.sla_deadline).toLocaleString('en-IN')}</span>
                  <span className="text-xs opacity-80">(Working hours Mon–Fri 8:00–19:00)</span>
                </>
              )}
            </div>
          );
        })()}

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap">{ticket.description}</p>
                
                {/* Location */}
                {ticket.ticket_location && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <span className="font-medium">Location:</span>
                    <span className="bg-muted px-2 py-0.5 rounded">{ticket.ticket_location}</span>
                  </div>
                )}

                {/* Repairs & Maintenance details */}
                {ticket.maintenance_category && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <span className="font-medium">Maintenance Category:</span>
                    <span className="bg-muted px-2 py-0.5 rounded">
                      {ticket.maintenance_category}{ticket.maintenance_other_text ? ` - ${ticket.maintenance_other_text}` : ''}
                    </span>
                  </div>
                )}
                
                {ticket.attachments?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-2">Attachments</h4>
                    <div className="flex flex-wrap gap-2">
                      {ticket.attachments.map((url, index) => {
                        const fileName = url.split('/').pop();
                        const handleDownload = async () => {
                          // Read from AuthContext (works regardless of localStorage / sessionStorage)
                          const token = sessionToken || localStorage.getItem('session_token') || sessionStorage.getItem('session_token') || '';
                          const downloadUrl = `${BACKEND_URL}/api/files/${fileName}?token=${encodeURIComponent(token)}`;
                          try {
                            const res = await fetch(downloadUrl);
                            if (!res.ok) throw new Error('Download failed');
                            const blob = await res.blob();
                            const disposition = res.headers.get('Content-Disposition');
                            let realName = fileName;
                            if (disposition) {
                              const match = disposition.match(/filename="?([^"]+)"?/);
                              if (match) realName = match[1];
                            }
                            const blobUrl = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.style.display = 'none';
                            a.href = blobUrl;
                            a.download = realName;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                          } catch (err) {
                            console.error('Download error:', err);
                            window.open(downloadUrl, '_blank');
                          }
                        };
                        return (
                          <button
                            key={index}
                            onClick={handleDownload}
                            className="flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-md cursor-pointer hover:bg-muted/80 transition-colors"
                            data-testid={`attachment-download-${index}`}
                          >
                            <Download className="w-3.5 h-3.5 text-primary" />
                            <span className="text-sm text-primary">{fileName}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Request-Specific Details */}
            {renderRequestDetails()}

            {/* Rejection Reason */}
            {ticket.status === 'Rejected' && ticket.rejection_reason && (
              <Card className="border-destructive/50 bg-destructive/5">
                <CardHeader>
                  <CardTitle className="text-destructive flex items-center gap-2">
                    <XCircle className="w-5 h-5" />
                    Rejection Reason
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{ticket.rejection_reason}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Rejected by: {ticket.approver_name}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Status History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-4">
                    {ticket.status_history?.map((entry, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                          {index < ticket.status_history.length - 1 && (
                            <div className="w-0.5 h-full bg-border" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <StatusBadge status={entry.status} />
                            <span className="text-sm text-muted-foreground">
                              by {entry.by}
                            </span>
                          </div>
                          {entry.remarks && (
                            <p className="text-sm mt-1">{entry.remarks}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDateTime(entry.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Employee Details */}
            <Card data-testid="employee-details-card">
              <CardHeader>
                <CardTitle className="text-base">Employee Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span data-testid="employee-name">{ticket.requester_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-muted-foreground" />
                  <span data-testid="employee-department">{ticket.department || ticket.requester_department}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>{formatDate(ticket.created_at)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Chat Section - moved to sidebar */}
            {canChat() && (
              <Card data-testid="ticket-chat-section">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Ticket Chat
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <ScrollArea className="h-[280px] pr-3">
                      {chatMessages.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground">
                          <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />
                          <p className="text-xs">No messages yet.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {chatMessages.map((msg) => {
                            const isMe = msg.sender_email === employee?.email;
                            return (
                              <div
                                key={msg.message_id}
                                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                                data-testid={`chat-msg-${msg.message_id}`}
                              >
                                <div
                                  className={`max-w-[85%] rounded-lg px-3 py-2 ${
                                    isMe
                                      ? 'bg-primary text-primary-foreground'
                                      : 'bg-muted'
                                  }`}
                                >
                                  {!isMe && (
                                    <p className="text-xs font-medium mb-1 opacity-80">
                                      {msg.sender_name}
                                      {msg.sender_role === 'Admin Head' && (
                                        <Badge variant="outline" className="ml-1 text-[10px] py-0">Admin</Badge>
                                      )}
                                      {msg.sender_role === 'Super Admin' && (
                                        <Badge variant="outline" className="ml-1 text-[10px] py-0">Super Admin</Badge>
                                      )}
                                      {msg.sender_role === 'Admin Team' && (
                                        <Badge variant="outline" className="ml-1 text-[10px] py-0">Admin</Badge>
                                      )}
                                      {msg.sender_role === 'IT Head' && (
                                        <Badge variant="outline" className="ml-1 text-[10px] py-0">IT Head</Badge>
                                      )}
                                      {msg.sender_role === 'IT Team' && (
                                        <Badge variant="outline" className="ml-1 text-[10px] py-0">IT</Badge>
                                      )}
                                    </p>
                                  )}
                                  <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                                  <p className={`text-[10px] mt-1 ${isMe ? 'opacity-70' : 'text-muted-foreground'}`}>
                                    {new Date(msg.created_at).toLocaleString('en-IN', {
                                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>

                    <div className="flex gap-2">
                      <Input
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        placeholder="Type your message..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendChat();
                          }
                        }}
                        disabled={chatSending}
                        data-testid="chat-message-input"
                        className="text-sm"
                      />
                      <Button
                        onClick={handleSendChat}
                        disabled={chatSending || !chatMessage.trim()}
                        size="icon"
                        data-testid="send-chat-btn"
                      >
                        {chatSending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* IT Internal Comments — visible only to IT Team/Head/Super Admin on IT tickets */}
            {ticket.module === 'IT' && isITRole && (
              <Card className="border-amber-200 dark:border-amber-900" data-testid="it-comments-card">
                <CardHeader className="bg-amber-50/60 dark:bg-amber-950/30">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Lock className="w-4 h-4 text-amber-600" />
                    Internal Comments
                    <Badge variant="outline" className="ml-2 text-[10px] py-0">IT-only</Badge>
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Not visible to the requester.</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="it-comments-list">
                    {itComments.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">No internal comments yet.</p>
                    ) : (
                      itComments.map(c => (
                        <div key={c.comment_id} className="rounded-md border bg-card p-2.5" data-testid={`it-comment-${c.comment_id}`}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold">{c.author_name}</span>
                            <span className="text-muted-foreground">{new Date(c.created_at).toLocaleString('en-IN')}</span>
                          </div>
                          <p className="text-sm mt-1 whitespace-pre-wrap">{c.message}</p>
                          <span className="text-[10px] text-muted-foreground">{c.author_role}</span>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={itCommentDraft}
                      onChange={(e) => setITCommentDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendITComment(); } }}
                      placeholder="Add an internal note…"
                      disabled={itCommentSending}
                      data-testid="it-comment-input"
                    />
                    <Button
                      size="icon"
                      onClick={handleSendITComment}
                      disabled={itCommentSending || !itCommentDraft.trim()}
                      data-testid="it-comment-send-btn"
                    >
                      {itCommentSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Requester Info - below chat */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Requester</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>{ticket.requester_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-muted-foreground" />
                  <span>{ticket.department || ticket.requester_department}</span>
                </div>
                {ticket.requester_details?.line_manager_name && (
                  <div className="text-sm text-muted-foreground">
                    Manager: {ticket.requester_details.line_manager_name}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Approver Info - below requester */}
            {ticket.approver_name && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Approver</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{ticket.approver_name}</span>
                  </div>
                  {ticket.approval_remarks && (
                    <p className="text-sm text-muted-foreground">
                      "{ticket.approval_remarks}"
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Assigned Admin */}
            {ticket.assigned_admin_name && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Assigned To</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{ticket.assigned_admin_name}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rating Display */}
            {ticket.rating && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Rating</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`w-5 h-5 ${
                          star <= ticket.rating 
                            ? 'fill-yellow-400 text-yellow-400' 
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="ml-2 font-semibold">{ticket.rating}/5</span>
                  </div>
                  {ticket.rating_comment && (
                    <p className="text-sm text-muted-foreground mt-2">
                      "{ticket.rating_comment}"
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Rated by {ticket.rated_by}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Cancellation Info */}
            {ticket.status === 'Cancelled' && ticket.cancellation_reason && (
              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="text-base text-destructive">Cancellation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{ticket.cancellation_reason}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Cancelled by {ticket.cancelled_by}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Approve Dialog */}
        <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Ticket</DialogTitle>
              <DialogDescription>
                This will approve the ticket and send it to the admin team queue.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Textarea
                  value={approvalRemarks}
                  onChange={(e) => setApprovalRemarks(e.target.value)}
                  placeholder="Add any remarks for approval..."
                  data-testid="approval-remarks-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproveDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleApprove} 
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-approve-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Ticket</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejection. This will be visible to the requester.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter the reason for rejection..."
                  data-testid="rejection-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleReject} 
                disabled={actionLoading}
                data-testid="confirm-reject-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Update Dialog */}
        <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Status</DialogTitle>
              <DialogDescription>
                Change the ticket status and add optional remarks.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select value={newStatus} onValueChange={(v) => {
                  setNewStatus(v);
                  if (v !== 'Completed') {
                    setCompletionCategory('');
                    setCompletionSubcategory('');
                  }
                }}>
                  <SelectTrigger data-testid="new-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {ticket?.module === 'IT' ? (
                      <>
                        <SelectItem value="Open" data-testid="status-opt-open">Open</SelectItem>
                        <SelectItem value="In Progress" data-testid="status-opt-in-progress">In Progress</SelectItem>
                        <SelectItem value="Completed" data-testid="status-opt-completed">Completed</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {ticket?.module === 'IT' && newStatus === 'Completed' && (
                <>
                  <div className="space-y-2">
                    <Label>
                      Category <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={completionCategory}
                      onValueChange={(v) => {
                        setCompletionCategory(v);
                        setCompletionSubcategory('');
                      }}
                    >
                      <SelectTrigger data-testid="completion-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(new Set(itTaxonomy.map((r) => r.category))).sort().map((cat) => (
                          <SelectItem key={cat} value={cat} data-testid={`completion-category-opt-${cat}`}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Sub Category <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={completionSubcategory}
                      onValueChange={setCompletionSubcategory}
                      disabled={!completionCategory}
                    >
                      <SelectTrigger data-testid="completion-subcategory-select">
                        <SelectValue placeholder={completionCategory ? 'Select sub category' : 'Select category first'} />
                      </SelectTrigger>
                      <SelectContent>
                        {itTaxonomy
                          .filter((r) => r.category === completionCategory)
                          .map((r) => (
                            <SelectItem key={r.id || `${r.category}-${r.subcategory}`} value={r.subcategory} data-testid={`completion-subcategory-opt-${r.subcategory}`}>
                              {r.subcategory}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Required to categorize the resolved IT issue.
                    </p>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Textarea
                  value={statusRemarks}
                  onChange={(e) => setStatusRemarks(e.target.value)}
                  placeholder="Add any remarks..."
                  data-testid="status-remarks-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleStatusUpdate} 
                disabled={actionLoading}
                data-testid="confirm-status-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Dialog */}
        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Ticket</DialogTitle>
              <DialogDescription>
                Select an admin team member to handle this ticket.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Admin Team Member</Label>
                <Select value={selectedAdmin} onValueChange={setSelectedAdmin}>
                  <SelectTrigger data-testid="assign-admin-select">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {adminTeam.map((admin) => (
                      <SelectItem key={admin.employee_id} value={admin.employee_id}>
                        {admin.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssign} 
                disabled={actionLoading}
                data-testid="confirm-assign-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Assign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove from Escalation Dialog */}
        <Dialog open={deEscalateDialog} onOpenChange={setDeEscalateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <ArrowDownCircle className="w-5 h-5" />
                Remove from Escalation
              </DialogTitle>
              <DialogDescription>
                The ticket will move back to <strong>{ticket.assigned_it_email ? 'In Progress' : 'Open'}</strong>. The requester and assigned IT user will be notified.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Reason <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Textarea
                  value={deEscalateReason}
                  onChange={(e) => setDeEscalateReason(e.target.value)}
                  placeholder="Why is this ticket being removed from escalation?"
                  rows={3}
                  data-testid="de-escalate-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeEscalateDialog(false)} data-testid="de-escalate-cancel-btn">
                Back
              </Button>
              <Button
                onClick={handleDeEscalate}
                disabled={actionLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="de-escalate-confirm-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Remove from Escalation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Cancel Dialog */}
        <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Ban className="w-5 h-5" />
                Cancel Request
              </DialogTitle>
              <DialogDescription>
                This action will cancel the request.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Cancellation Reason *</Label>
                <Textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Reason for cancellation..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialog(false)}>
                Back
              </Button>
              <Button 
                variant="destructive"
                onClick={handleCancel}
                disabled={actionLoading || !cancellationReason.trim()}
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Cancel Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rating Dialog */}
        <Dialog open={ratingDialog} onOpenChange={setRatingDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Rate Service
              </DialogTitle>
              <DialogDescription>
                How was your experience with this request?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => { setRating(star); setRatingComment(''); setRatingAdditionalComments(''); }}
                    className="p-1 hover:scale-110 transition-transform"
                    data-testid={`rating-star-${star}`}
                  >
                    <Star 
                      className={`w-10 h-10 ${
                        star <= rating 
                          ? 'fill-yellow-400 text-yellow-400' 
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {rating === 0 && 'Click to rate'}
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </p>
              <div className="space-y-2">
                <Label>Feedback *</Label>
                <Select
                  value={ratingComment}
                  onValueChange={(v) => { setRatingComment(v); if (v !== 'Other') setRatingAdditionalComments(''); }}
                  disabled={rating === 0}
                >
                  <SelectTrigger data-testid="rating-feedback-dropdown">
                    <SelectValue placeholder={rating === 0 ? 'Select a rating first' : 'Select your feedback...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(RATING_OPTIONS[rating] || []).map(opt => (
                      <SelectItem key={opt} value={opt} data-testid={`rating-option-${opt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rating === 1 && ratingComment === 'Other' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Additional Comments *</Label>
                    <Textarea
                      value={ratingAdditionalComments}
                      onChange={(e) => setRatingAdditionalComments(e.target.value)}
                      placeholder="Please describe your feedback..."
                      rows={3}
                      data-testid="rating-additional-comments"
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRatingDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleRating}
                disabled={actionLoading || rating === 0}
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Submit Rating
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reassign Dialog — IT tickets */}
        <Dialog open={reassignDialog} onOpenChange={setReassignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" /> Reassign IT Ticket
              </DialogTitle>
              <DialogDescription>
                Reassign to any IT Head or IT Team member across all locations. SLA timer will reset to 4 hours from now.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="text-xs text-muted-foreground">
                Current assignee: <span className="font-medium text-foreground">{ticket?.assigned_it_name || 'Unassigned'}</span>
                {ticket?.assigned_it_email && <> ({ticket.assigned_it_email})</>}
              </div>
              <div className="space-y-1">
                <Label>New Assignee *</Label>
                <select
                  value={reassignTarget}
                  onChange={e => setReassignTarget(e.target.value)}
                  className="w-full border rounded-md px-3 h-9 text-sm bg-background"
                  data-testid="reassign-select"
                >
                  <option value="">Select a user...</option>
                  {reassignableUsers.map(u => (
                    <option key={u.email} value={u.email}>
                      {u.full_name} ({u.role}) — {u.location}
                    </option>
                  ))}
                </select>
                {reassignableUsers.length === 0 && (
                  <p className="text-xs text-amber-600">No other IT users available to reassign.</p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Reason (optional)</Label>
                <Textarea
                  value={reassignReason}
                  onChange={e => setReassignReason(e.target.value)}
                  placeholder="Why are you reassigning this ticket?"
                  rows={3}
                  data-testid="reassign-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReassignDialog(false)}>Cancel</Button>
              <Button onClick={submitReassign} disabled={actionLoading || !reassignTarget} data-testid="reassign-submit-btn">
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Reassign Ticket
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
